import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import { Link } from 'react-router-dom';
import type {
  RentalContract,
  GetRentalContractsQuery,
  RentalContractStatus
} from '../../types/rental';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bgBase: '#faf7ee',
  bgCard: '#ffffff',
  bgElevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  borderNormal: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const thStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 9,
  letterSpacing: '0.22em',
  color: C.textMuted,
  textTransform: 'uppercase',
  padding: '10px 16px',
  background: C.bgElevated,
  borderBottom: `1px solid ${C.borderSubtle}`,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 12,
  color: C.textSecondary,
  padding: '11px 16px',
  borderBottom: `1px solid ${C.borderNormal}`,
  whiteSpace: 'nowrap',
};

const statusBadgeStyle = (status: RentalContractStatus): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    ACTIVE:     { bg: 'rgba(90,201,140,0.15)',  color: '#5ac98c', border: 'rgba(90,201,140,0.3)'  },
    EXPIRED:    { bg: 'rgba(201,90,90,0.15)',   color: '#e87a7a', border: 'rgba(201,90,90,0.3)'   },
    TERMINATED: { bg: 'rgba(120,120,140,0.15)', color: '#a0a0b8', border: 'rgba(120,120,140,0.3)' },
    DRAFT:      { bg: 'rgba(92,78,51,0.22)',  color: '#fbbf24', border: 'rgba(217,119,6,0.25)'  },
    RENEWED:    { bg: 'rgba(90,140,201,0.15)',  color: '#7ab0e8', border: 'rgba(90,140,201,0.3)'  },
  };
  const s = map[status] || map.TERMINATED;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 10,
    fontFamily: C.fontLabel,
    letterSpacing: '0.14em',
    fontWeight: 600,
    textTransform: 'uppercase',
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.border}`,
  };
};

export const RentalList: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<GetRentalContractsQuery>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined
  });
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['rental-contracts', filters],
    queryFn: () => rentalService.getRentalContracts(filters)
  });

  const deleteContractMutation = useMutation({
    mutationFn: rentalService.deleteRentalContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
    }
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status: RentalContractStatus | undefined) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDeleteContract = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this rental contract? This action cannot be undone.')) {
      try {
        await deleteContractMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting contract:', error);
        alert('Failed to delete contract. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, padding: 24 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ height: 48, background: C.bgElevated, borderRadius: 4, marginBottom: 10, opacity: 0.6 }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, padding: 32, textAlign: 'center' }}>
        <p style={{ fontFamily: C.fontBody, color: '#e87a7a', margin: 0 }}>Error loading rental contracts. Please try again.</p>
      </div>
    );
  }

  const contracts = data?.data || [];
  const total = data?.total || 0;
  const page = data?.page || 1;
  const limit = data?.limit || 10;
  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '32px 16px', background: C.bgBase, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>

        {/* ── Header ──────────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{
              fontFamily: C.fontHeading,
              fontSize: 34,
              fontWeight: 300,
              color: C.textPrimary,
              letterSpacing: '0.04em',
              margin: 0,
              lineHeight: 1.1,
            }}>
              Rental Contracts
            </h1>
            <div style={{ width: 56, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginTop: 8 }} />
          </div>
          <Link
            to="/rentals/new"
            style={{
              fontFamily: C.fontLabel,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '10px 24px',
              borderRadius: 4,
              border: 'none',
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              color: C.bgCard,
              textDecoration: 'none',
              display: 'inline-block',
              fontWeight: 700,
            }}
          >
            + New Contract
          </Link>
        </div>

        {/* ── Card ────────────────────────────────────────────────────────────── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, overflow: 'hidden' }}>

          {/* Filters Bar */}
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search contracts..."
                  value={filters.search}
                  onChange={handleSearch}
                  style={{
                    paddingLeft: 36,
                    paddingRight: 14,
                    paddingTop: 9,
                    paddingBottom: 9,
                    background: C.bgBase,
                    border: `1px solid ${C.borderSubtle}`,
                    borderRadius: 4,
                    color: C.textPrimary,
                    fontFamily: C.fontBody,
                    fontSize: 12,
                    outline: 'none',
                    width: 220,
                  }}
                />
                <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <svg width={14} height={14} fill="none" stroke={C.textMuted} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Status Filter */}
              <select
                value={filters.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value as RentalContractStatus || undefined)}
                style={{
                  padding: '9px 14px',
                  background: C.bgBase,
                  border: `1px solid ${C.borderSubtle}`,
                  borderRadius: 4,
                  color: C.textPrimary,
                  fontFamily: C.fontBody,
                  fontSize: 12,
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="TERMINATED">Terminated</option>
                <option value="RENEWED">Renewed</option>
              </select>
            </div>

            <span style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted }}>
              Showing {contracts.length} of {total} contracts
            </span>
          </div>

          {/* Empty State */}
          {contracts.length === 0 ? (
            <div style={{ padding: '56px 24px', textAlign: 'center' }}>
              <p style={{ fontFamily: C.fontHeading, fontSize: 22, fontWeight: 300, color: C.textMuted, margin: '0 0 8px 0' }}>
                No rental contracts found
              </p>
              <p style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textMuted, margin: '0 0 24px 0' }}>
                Get started by creating your first rental contract
              </p>
              <Link
                to="/rentals/new"
                style={{
                  fontFamily: C.fontLabel,
                  fontSize: 9,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  padding: '10px 28px',
                  borderRadius: 4,
                  border: 'none',
                  background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                  color: C.bgCard,
                  textDecoration: 'none',
                  display: 'inline-block',
                  fontWeight: 700,
                }}
              >
                Create Contract
              </Link>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Contract #</th>
                      <th style={thStyle}>Customer</th>
                      <th style={thStyle}>Machines</th>
                      <th style={thStyle}>Start Date</th>
                      <th style={thStyle}>End Date</th>
                      <th style={thStyle}>Monthly Fee</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract: RentalContract) => (
                      <tr
                        key={contract.id}
                        onMouseEnter={() => setHoveredRow(contract.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ background: hoveredRow === contract.id ? C.bgElevated : 'transparent', transition: 'background 0.15s' }}
                      >
                        <td style={tdStyle}>
                          <Link
                            to={`/rentals/${contract.id}`}
                            style={{ color: C.gold, textDecoration: 'none', fontFamily: C.fontBody, fontSize: 12, fontWeight: 500 }}
                          >
                            {contract.contractNumber}
                          </Link>
                        </td>
                        <td style={{ ...tdStyle, color: C.textPrimary }}>{contract.customer?.name || 'N/A'}</td>
                        <td style={tdStyle}>{contract.machines?.length || 0}</td>
                        <td style={tdStyle}>{new Date(contract.startDate).toLocaleDateString()}</td>
                        <td style={tdStyle}>{new Date(contract.endDate).toLocaleDateString()}</td>
                        <td style={{ ...tdStyle, color: C.textPrimary, fontWeight: 500 }}>
                          BD {(contract.machines?.reduce((total, machine) => total + (machine.monthlyRentalFee || 0), 0) || 0).toFixed(2)}
                        </td>
                        <td style={tdStyle}>
                          <span style={statusBadgeStyle(contract.status)}>{contract.status}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 14 }}>
                            <Link
                              to={`/rentals/${contract.id}`}
                              style={{ fontFamily: C.fontBody, fontSize: 12, color: C.gold, textDecoration: 'none' }}
                            >
                              View
                            </Link>
                            <Link
                              to={`/rentals/${contract.id}/edit`}
                              style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, textDecoration: 'none' }}
                            >
                              Edit
                            </Link>
                            {contract.status === 'DRAFT' && (
                              <button
                                onClick={() => handleDeleteContract(contract.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontFamily: C.fontBody,
                                  fontSize: 12,
                                  color: '#e87a7a',
                                  padding: 0,
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted }}>
                    Page {page} of {totalPages}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      onMouseEnter={() => setHoveredBtn('prev')}
                      onMouseLeave={() => setHoveredBtn(null)}
                      style={{
                        fontFamily: C.fontLabel,
                        fontSize: 9,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        padding: '7px 16px',
                        borderRadius: 4,
                        border: `1px solid ${C.borderSubtle}`,
                        background: hoveredBtn === 'prev' && page !== 1 ? C.bgElevated : 'transparent',
                        color: page === 1 ? C.textMuted : C.textSecondary,
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.5 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      onMouseEnter={() => setHoveredBtn('next')}
                      onMouseLeave={() => setHoveredBtn(null)}
                      style={{
                        fontFamily: C.fontLabel,
                        fontSize: 9,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        padding: '7px 16px',
                        borderRadius: 4,
                        border: `1px solid ${C.borderSubtle}`,
                        background: hoveredBtn === 'next' && page !== totalPages ? C.bgElevated : 'transparent',
                        color: page === totalPages ? C.textMuted : C.textSecondary,
                        cursor: page === totalPages ? 'not-allowed' : 'pointer',
                        opacity: page === totalPages ? 0.5 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
